import { useState, type ReactNode } from 'react';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Amount, Button, Chip, Input, Panel, PanelHeader, Ref } from '@promanage/ui';
import { REF_PREFIX, formatNzDate, formatPeriod, formatRef, parseRef } from '@promanage/core';
import {
  MIN_CANCEL_REASON_LENGTH,
  MIN_REVERSE_REASON_LENGTH,
  RUN_REVERSAL_LABELS,
} from '@promanage/core/disbursement';
import { RecordNotFound } from '../components/RecordNotFound';
import { RecordIdentity } from '../components/RecordIdentity';
import { RecordSkeleton } from '../components/RecordSkeleton';
import { TopBar } from '../components/TopBar';
import { Icon } from '../components/Icon';
import { BankFileDialog } from '../features/disbursements/BankFileDialog';
import { RunLines } from '../features/disbursements/RunLines';
import { RunStatements } from '../features/disbursements/RunStatements';
import { RUN_TONE, runNextStep, type RunStatus } from '../features/disbursements/status';
import { trpc, type RouterOutputs } from '../lib/trpc';
import { whenText } from '../features/automations/format';

type Run = RouterOutputs['disbursement']['get'];

export const Route = createFileRoute('/financial/disbursements/$ref')({
  beforeLoad: ({ params }) => {
    const parsed = parseRef(params.ref);
    if (parsed?.entity === 'disbursement') {
      const canonical = formatRef('disbursement', parsed.sequence);
      // Loop-safe: formatRef of a parsed canonical ref is that same string.
      if (canonical !== params.ref) {
        throw redirect({
          to: '/financial/disbursements/$ref',
          params: { ref: canonical },
          replace: true,
        });
      }
    }
  },
  component: RunPage,
});

/**
 * One payment run, addressed by its DB number: /financial/disbursements/DB000007.
 *
 * THE REF IS WHAT A PERSON READS. It is the number quoted down the phone to the bank, written on a
 * remittance and searched for six weeks later, and it is what every other record in this product
 * puts in the address bar. This screen carried a uuid until it did not, and the argument for that
 * — "nobody types a run number in" — was answered by the first person who wanted to send a
 * colleague a link to the run they were both looking at.
 *
 * Same ref contract as /owners/$ref: `beforeLoad` normalises anything ref-shaped to the canonical
 * form, and the component answers each remaining wrong shape distinctly, because a ref for another
 * entity, a typo and a number this agency never issued are three different mistakes.
 */
function RunPage() {
  const { ref } = Route.useParams();
  const parsed = parseRef(ref);

  if (!parsed) {
    return (
      <Unresolved title="Not a record reference">
        <code className="font-mono text-ink">{ref}</code> is not a record reference. Payment runs
        look like <code className="font-mono text-ink">DB000007</code> — two letters for the record
        type, then its number.
      </Unresolved>
    );
  }

  if (parsed.entity !== 'disbursement') {
    return (
      <Unresolved title={`${formatRef(parsed.entity, parsed.sequence)} is not a payment run`}>
        {/* "an invoice", "an inspection" — the entity name is data, so the article is derived. */}
        That is {/^[aeiou]/.test(parsed.entity) ? 'an' : 'a'} {parsed.entity} reference. Payment
        runs start with <code className="font-mono text-ink">{REF_PREFIX.disbursement}</code>.
      </Unresolved>
    );
  }

  return <RunDetail refSeq={parsed.sequence} />;
}

/**
 * Propose → review → approve → execute, on one screen.
 *
 * THE FOUR STEPS ARE FOUR BUTTONS, one at a time, and never two. A screen offering both "review"
 * and "approve" would let one person walk a run from proposal to authorisation in two clicks,
 * which is the separation of duties in §5 dissolved by an interface rather than by a rule.
 *
 * THE URL CARRIES THE REF AND THE MUTATIONS CARRY THE ID. What this screen has once the run has
 * loaded is its uuid, and every write names that — a write addressed by a number a person could
 * increment is a different shape of thing from a read.
 */
function RunDetail({ refSeq }: { refSeq: number }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [makingBankFile, setMakingBankFile] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [reverseReason, setReverseReason] = useState('');
  const [reversed, setReversed] = useState<RouterOutputs['disbursement']['reverse'] | null>(null);

  const run = useQuery(
    trpc.disbursement.get.queryOptions(
      { refSeq },
      // Short: the approval hash is compared against the run as it stands, so a stale screen is a
      // screen that says a redirected payee is fine.
      { staleTime: 5_000 },
    ),
  );

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.disbursement.get.queryKey({ refSeq }) });
    void queryClient.invalidateQueries({ queryKey: trpc.disbursement.list.queryKey() });
  };

  const review = useMutation(
    trpc.disbursement.review.mutationOptions({
      meta: { errorTitle: 'Could not mark this run reviewed' },
      onSuccess: () => {
        refresh();
      },
    }),
  );

  const setLineStatus = useMutation(
    trpc.disbursement.setLineStatus.mutationOptions({
      meta: { errorTitle: 'Could not change that line' },
      onSuccess: () => {
        refresh();
      },
    }),
  );

  const approve = useMutation(
    trpc.disbursement.approve.mutationOptions({
      meta: { errorTitle: 'Could not approve this run' },
      onSuccess: () => {
        refresh();
      },
    }),
  );

  const execute = useMutation(
    trpc.disbursement.execute.mutationOptions({
      meta: { errorTitle: 'Could not pay this run' },
      onSuccess: () => {
        refresh();
        // The trust position, the owner balances and the reconciliation working list all moved.
        void queryClient.invalidateQueries({ queryKey: trpc.ledger.position.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.dashboard.summary.queryKey() });
        /*
         * AND THE FILE, OFFERED IMMEDIATELY. Paying a run posts the journals and leaves the money
         * still sitting in the bank account — the owners are paid in the BOOKS and nothing has told
         * the bank. An operator who closes this screen at that point has a trust account that will
         * not reconcile and no prompt anywhere to say why, so the next step opens by itself rather
         * than waiting to be found in the top bar.
         *
         * It is a dialog rather than an automatic download: the format and the processing date are
         * still a decision, and a file that appeared in somebody's downloads folder unasked is one
         * nobody checked the three figures on.
         */
        setMakingBankFile(true);
      },
    }),
  );

  const cancel = useMutation(
    trpc.disbursement.cancel.mutationOptions({
      meta: { errorTitle: 'Could not cancel this run' },
      onSuccess: () => {
        refresh();
        void navigate({ to: '/financial/disbursements' });
      },
    }),
  );

  const reverse = useMutation(
    trpc.disbursement.reverse.mutationOptions({
      meta: { errorTitle: 'Could not reverse this run' },
      onSuccess: (result) => {
        refresh();
        // The same screens `execute` moves, moved back.
        void queryClient.invalidateQueries({ queryKey: trpc.ledger.position.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.dashboard.summary.queryKey() });
        setReversing(false);
        setReverseReason('');
        setReversed(result);
      },
    }),
  );

  if (run.isPending) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Disbursement run" subtitle="Loading…" />
        <RecordSkeleton />
      </div>
    );
  }

  if (run.error || !run.data) {
    return (
      <Unresolved title={`No run ${formatRef('disbursement', refSeq)}`}>
        Nothing in this agency uses that number. It may belong to a different agency — references
        are numbered per agency, so the same code means different records elsewhere.
      </Unresolved>
    );
  }

  const detail = run.data;
  const runId = detail.id;
  const status = detail.status as RunStatus;
  const editable = status === 'proposed' || status === 'reviewed';
  const busy =
    review.isPending ||
    approve.isPending ||
    execute.isPending ||
    cancel.isPending ||
    reverse.isPending ||
    setLineStatus.isPending;

  // §5. Null while the run has never been approved; false once something about where the money
  // goes has moved since it was.
  const tampered = detail.contentsMatchApproval === false;

  // Payments this run posted that nothing has reversed yet — what a reversal would undo.
  const standing = detail.lines.filter(
    (line) => line.transactionId !== null && line.reversedByRef === null,
  ).length;

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title={detail.ref}
        /*
         * WHO IT PAYS, FIRST. It decides what every figure under it means — a total against owners
         * is money leaving beneficiaries their own balances, a total against suppliers is invoices
         * being settled — and somebody arriving on this page from a link, a ref typed into the
         * address bar or a support ticket has nothing else to tell them which they are looking at.
         */
        subtitle={`${detail.kindLabel} · ${detail.trustBankAccountName} · struck as at ${formatNzDate(detail.asAt)}`}
        actions={
          <>
            <BackActions />

            {status === 'proposed' ? (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  review.mutate({ runId });
                }}
              >
                Mark reviewed
              </Button>
            ) : null}

            {status === 'reviewed' ? (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  approve.mutate({ runId });
                }}
              >
                Approve
              </Button>
            ) : null}

            {status === 'approved' ? (
              <Button
                size="sm"
                disabled={busy || tampered}
                onClick={() => {
                  execute.mutate({ runId });
                }}
              >
                {execute.isPending ? 'Paying' : 'Pay this run'}
              </Button>
            ) : null}

            {/*
             * THE FILE IS OFFERED ONLY ONCE THE RUN HAS BEEN PAID, and that ordering is the whole
             * safety of it. A file made from an approved-but-unexecuted run would be a batch at
             * the bank with no journals behind it — the money out of the account, the books saying
             * the agency still held it, and invariant 2 broken by our own export button.
             *
             * It stays available afterwards rather than being a one-time step on the way out of
             * execution: a file gets rejected, lost, or loaded at the wrong bank, and the answer
             * to all three is to make it again from the payments that are already posted.
             */}
            {status === 'executed' && standing > 0 ? (
              <Button
                size="sm"
                onClick={() => {
                  setMakingBankFile(true);
                }}
              >
                Bank file
              </Button>
            ) : null}

            {/*
             * ONLY WHILE SOMETHING IS LEFT TO UNDO. A run whose every payment is reversed has
             * nothing for the button to do, and offering it would invite pressing it to find out.
             */}
            {status === 'executed' && standing > 0 ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  setReversing((was) => !was);
                }}
              >
                {reversing ? 'Keep it' : 'Reverse run'}
              </Button>
            ) : null}

            {editable || status === 'approved' ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setCancelling((was) => !was);
                }}
              >
                {cancelling ? 'Keep it' : 'Cancel run'}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="flex flex-col gap-4">
        {tampered ? (
          /*
           * THE ONE RED ALARM ON THIS SCREEN, and the only place in the feature that earns red —
           * DESIGN_SYSTEM.md §3 reserves it for error and destruction, and this is the software
           * reporting that an authorised payment instruction no longer matches what was
           * authorised. Said BEFORE the button is pressed: a refusal at the moment of execution
           * is correct and useless, because by then somebody has told the office it is going out.
           */
          <Panel className="flex items-start gap-3 p-4 ring-1 ring-danger/50">
            <Icon name="alert" className="mt-0.5 shrink-0 text-danger" size={18} />
            <div className="text-[0.8125rem]">
              <p className="font-semibold text-ink">
                This run has changed since it was approved and cannot be paid.
              </p>
              <p className="mt-1 text-ink-muted">
                An amount, {detail.kind === 'supplier' ? 'a bill' : 'an owner'}, or a bank account
                is not what was signed off — look for the lines marked “account changed” below.
                Cancel this run and propose another; nothing on an approved run can be edited, on
                purpose.
              </p>
            </div>
          </Panel>
        ) : null}

        {cancelling ? (
          <Panel className="flex flex-wrap items-end gap-2 p-4">
            <Input
              autoFocus
              aria-label="Why this run is being cancelled"
              placeholder="Why is this run being cancelled?"
              value={cancelReason}
              onChange={(event) => {
                setCancelReason(event.target.value);
              }}
              className="min-w-64 flex-1"
            />
            <Button
              size="sm"
              variant="danger"
              disabled={busy || cancelReason.trim().length < MIN_CANCEL_REASON_LENGTH}
              onClick={() => {
                cancel.mutate({ runId, reason: cancelReason.trim() });
              }}
            >
              Cancel this run
            </Button>
          </Panel>
        ) : null}

        {reversing ? (
          /*
           * THE CONSEQUENCE, BEFORE THE BUTTON. Reversing puts the money back on the ledgers it was
           * paid from, and the next run pays it out again — correct when the credits never left the
           * bank, and a double payment when they did. That is the one thing the operator has to be
           * sure of, so it is the first sentence rather than a confirmation dialog after the fact.
           */
          <Panel className="flex flex-col gap-3 p-4 ring-1 ring-warn/40">
            <div className="flex items-start gap-3 text-[0.8125rem]">
              <Icon name="alert" className="mt-0.5 shrink-0 text-warn" size={18} />
              <div>
                <p className="font-semibold text-ink">
                  Only reverse this run if the money did not leave the trust account.
                </p>
                <p className="mt-1 text-ink-muted">
                  The file was never loaded, the bank rejected it, or the credits came back. Each of
                  the {standing} {standing === 1 ? 'payment' : 'payments'} still standing is
                  mirrored by a reversing transaction, and{' '}
                  {detail.kind === 'supplier'
                    ? 'the bills return to the queue for the next supplier run — their owners stay charged, because the invoices are still owed.'
                    : 'the money goes back on each owner’s balance, to be paid on the next run.'}{' '}
                  It counts as cleared once the reconciliation has ticked the payments and their
                  reversals off.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <Input
                autoFocus
                aria-label="Why this run is being reversed"
                placeholder="Why is this run being reversed?"
                value={reverseReason}
                onChange={(event) => {
                  setReverseReason(event.target.value);
                }}
                className="min-w-64 flex-1"
              />
              <Button
                size="sm"
                variant="danger"
                disabled={busy || reverseReason.trim().length < MIN_REVERSE_REASON_LENGTH}
                onClick={() => {
                  reverse.mutate({ runId, reason: reverseReason.trim() });
                }}
              >
                {reverse.isPending
                  ? 'Reversing'
                  : `Reverse ${String(standing)} ${standing === 1 ? 'payment' : 'payments'}`}
              </Button>
            </div>
          </Panel>
        ) : null}

        {reversed ? (
          <Panel className="flex items-start gap-3 p-4">
            <Icon name="check" className="mt-0.5 shrink-0 text-positive" size={18} />
            <p className="text-[0.8125rem] text-ink-muted">
              <span className="font-semibold text-ink">
                Reversed {reversed.reversals.length}{' '}
                {reversed.reversals.length === 1 ? 'payment' : 'payments'} ·{' '}
                {reversed.total.formatted}.
              </span>{' '}
              {reversed.alreadyReversedCount > 0
                ? `${String(reversed.alreadyReversedCount)} had already been reversed on their own and were left alone. `
                : ''}
              {reversed.lateItem && reversed.periodStartsOn
                ? `The month they were paid in is closed, so the reversals are in ${formatPeriod(reversed.periodStartsOn)}.`
                : ''}
            </p>
          </Panel>
        ) : null}

        <RunIdentity detail={detail} refSeq={refSeq} />

        <RunLines
          run={detail}
          editable={editable}
          busy={busy}
          onSetStatus={(lineId, lineStatus, reason) => {
            setLineStatus.mutate({
              runId,
              lineId,
              status: lineStatus,
              ...(reason === undefined ? {} : { reason }),
            });
          }}
        />

        <RunStatements run={detail} />

        <RunTrail detail={detail} />
      </div>

      {makingBankFile ? (
        <BankFileDialog
          source={{ kind: 'run', runId, ref: detail.ref }}
          onClose={() => {
            setMakingBankFile(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** The way out to the list. */
function BackActions() {
  return (
    <Link to="/financial/disbursements">
      <Button variant="secondary" size="sm">
        All disbursements
      </Button>
    </Link>
  );
}

/**
 * The shared identity card, as on a bill or a job, carrying the three figures that decide whether
 * this run can go. The total is the title because it is what the run IS.
 *
 * CLEARED FUNDS CAN SAY TWO THINGS — what the trust account had when the run was struck, and what
 * it has now. They are usually the same and the pair is what makes a difference visible: money
 * moved between the proposal and this moment, which is exactly the condition §8 says has to be
 * re-checked immediately before execution.
 */
function RunIdentity({ detail, refSeq }: { detail: Run; refSeq: number }) {
  const total = BigInt(detail.total.cents);
  // Null on a run that has paid or been cancelled — there is nothing left for "now" to decide, and
  // the trust account having less in it after a run paid out is not a shortfall.
  const clearedNow = detail.clearedNow === null ? null : BigInt(detail.clearedNow.cents);
  const short = clearedNow !== null && total > clearedNow;

  return (
    <RecordIdentity
      title={<Amount value={total} currency tone="neutral" />}
      symbol={<Icon name="payout" className="size-6" />}
      status={
        <>
          <Ref entity="disbursement" sequence={refSeq} />
          <Chip tone="neutral">{detail.kindLabel}</Chip>
          <Chip tone={RUN_TONE[detail.status as RunStatus]}>{detail.statusLabel}</Chip>
          {detail.reversal === 'none' ? null : (
            <Chip tone="neutral">{RUN_REVERSAL_LABELS[detail.reversal]}</Chip>
          )}
        </>
      }
      facts={[
        clearedNow === null
          ? {
              label: 'Cleared when the run was struck',
              value: <Amount value={BigInt(detail.clearedAtProposal.cents)} tone="neutral" />,
            }
          : {
              label: 'Cleared in the trust account',
              value: (
                <>
                  <Amount value={clearedNow} tone={short ? 'warn' : 'neutral'} />
                  {detail.clearedNow?.cents === detail.clearedAtProposal.cents ? null : (
                    <span className="mt-1 block text-[0.75rem] font-normal text-ink-subtle">
                      Was {detail.clearedAtProposal.formatted} when the run was struck
                    </span>
                  )}
                </>
              ),
            },
        {
          label: 'Next',
          value: (
            <>
              <span className="font-normal">
                {runNextStep(detail.status as RunStatus, detail.reversal)}
              </span>
              {detail.dualApprovalThreshold ? (
                <span className="mt-1 block text-[0.75rem] font-normal text-ink-subtle">
                  {detail.dualApprovalThreshold.cents === '0'
                    ? 'Approved by a second person, as this agency requires for every run'
                    : `Second approver required above ${detail.dualApprovalThreshold.formatted}`}
                </span>
              ) : null}
            </>
          ),
        },
      ]}
    />
  );
}

/**
 * Who did what, in order — the four-step trail an auditor reads.
 *
 * NAMES, NOT ROLES. "Approved by Sam" is what answers the separation-of-duties question; "approved
 * by a trust administrator" answers nothing, because the whole point is whether it was the same
 * person who proposed it.
 */
function RunTrail({ detail }: { detail: Run }) {
  const steps = [
    { label: 'Proposed', by: detail.proposedByName, at: detail.proposedAt },
    { label: 'Reviewed', by: detail.reviewedByName, at: detail.reviewedAt },
    { label: 'Approved', by: detail.approvedByName, at: detail.approvedAt },
    { label: 'Paid', by: detail.executedByName, at: detail.executedAt },
    // The most recent reversal — the reason is in the audit log with the rest of the event.
    { label: 'Reversed', by: detail.lastReversedByName, at: detail.lastReversedAt },
    { label: 'Cancelled', by: detail.cancelledByName, at: detail.cancelledAt },
    // A type predicate rather than a plain `!== null` filter, so the row below can read `step.at`
    // without an assertion. CLAUDE.md: prefer parsing over asserting, on every path.
  ].filter((step): step is { label: string; by: string | null; at: string } => step.at !== null);

  return (
    <Panel className="min-w-0 p-0">
      <PanelHeader title="Trail" className="px-4 pt-4 pb-2" />
      <ul className="border-t border-rule">
        {steps.map((step) => (
          <li
            key={step.label}
            className="flex flex-wrap items-baseline gap-x-3 border-b border-rule px-4 py-2.5 last:border-b-0"
          >
            <span className="w-20 text-[0.75rem] font-semibold text-ink-muted">{step.label}</span>
            <span className="flex-1 text-[0.8125rem]">{step.by ?? 'Unknown'}</span>
            <span className="text-[0.75rem] tabular-nums text-ink-subtle">{whenText(step.at)}</span>
          </li>
        ))}

        {detail.transactionRefs.length > 0 ? (
          <li className="flex flex-wrap items-baseline gap-x-3 border-b border-rule px-4 py-2.5 last:border-b-0">
            <span className="w-20 text-[0.75rem] font-semibold text-ink-muted">Posted</span>
            {/*
             * ONE TX PER PAYMENT, all of them listed. The run posts a transaction per destination —
             * the bank shows a single debit for the batch, and these are what it clears — so a
             * single reference here would be one payment of many pretending to be the run.
             *
             * The TX is the reference a person gets — DESIGN_SYSTEM.md §3's reference rule. The
             * journals behind each are one click in, on the transaction itself.
             */}
            <span className="flex flex-1 flex-wrap gap-x-3 gap-y-1">
              {detail.transactionRefs.map((ref) => (
                <Ref key={ref} entity="transaction" sequence={Number(ref.replace(/\D/g, ''))} />
              ))}
            </span>
          </li>
        ) : null}

        {detail.cancelReason ? (
          <li className="px-4 py-2.5 text-[0.8125rem] text-ink-muted">
            Reason: {detail.cancelReason}
          </li>
        ) : null}
      </ul>
    </Panel>
  );
}

/**
 * A ref that did not resolve — a typo, another entity's number, or one this agency never issued.
 *
 * ONE PANEL, THREE DIFFERENT SENTENCES, chosen by the caller. "Not found" for all three would
 * leave the operator who typed DB00007 doing the same thing again, and the one who pasted an owner
 * ref wondering whether the run had been deleted.
 */
function Unresolved({ title, children }: { title: string; children: ReactNode }) {
  return (
    <RecordNotFound
      record="Disbursement run"
      title={title}
      back={{ label: 'Back to disbursements', link: { to: '/financial/disbursements' } }}
    >
      {children}
    </RecordNotFound>
  );
}
